import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  MenuItem,
  SalvCard,
  SalvDropdown,
  SalvMenuButton,
  SalvProgressCircle,
  SalvTextArea,
  Size,
} from '@salvapp/ui-components';
import { Form, Formik } from 'formik';
import SalvPage from '../common/SalvPage';
import { InternalNavigation } from '../navigation/helper';
import { useArchiveCase, useQueryCase, useQueryCaseRationaleOptions, useUpdateCase } from './caseQueryActions';
import { CAN_ARCHIVE_CASE, CAN_MODIFY_CASE } from '../auth/permissions';
import Has from '../auth/Has';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../feedback/feedbackReducer';
import { formatDate } from '../common/datetime/FormattedDateTime';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import FieldEditor, { FieldView } from '../common/form/FieldEditor';
import { useMe } from '../organisation/useMe';
import { CaseFormValues, validationSchema } from './CaseCreation';
import CaseAlerts from './attachments/alerts/CaseAlerts';
import CaseTransactions from './attachments/transactions/CaseTransactions';
import CasePersons from './attachments/persons/CasePersons';
import CaseManagement from './management/CaseManagement';
import { scroller } from '../common/scroller';
import { Documents } from '../common/documents/Documents';
import { InternalApiUniversalDocumentEntityType } from '../api-internal';
import CaseNotes from './notes/CaseNotes';
import { CasePendingStatusProvider } from './management/CasePendingStatusContext';

const Case = () => {
  const handle = useParams<{ handle: string }>().handle!;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { permissions } = useMe();

  const updateCase = useUpdateCase(handle);
  const [editedField, setEditedField] = useState<string>();

  const archiveCase = useArchiveCase();

  const { data: caseData, isPending: isCasePending, isError: isCaseError, error: caseError } = useQueryCase(handle);
  const {
    data: rationaleOptions,
    isPending: isRationaleOptionsPending,
    isError: isRationaleOptionsError,
    error: rationaleOptionsError,
  } = useQueryCaseRationaleOptions();
  const initialValues = useMemo(
    () =>
      caseData
        ? {
            name: caseData.details.name,
            description: caseData.details.description,
            rationaleCode: caseData.details.rationaleCode,
          }
        : undefined,
    [caseData]
  );

  const scrollTo = (location.state as { scrollTo?: string })?.scrollTo;
  useEffect(() => {
    if (!isCasePending && scrollTo) {
      setTimeout(() => {
        scroller.scrollTo(scrollTo, { smooth: true, duration: 400, offset: -20 });
      }, 100);
    }
  }, [isCasePending, scrollTo]);

  if (isCaseError || isRationaleOptionsError) {
    return <DataLoadingErrorFallback message='Could not load Case' error={[caseError, rationaleOptionsError]} />;
  }

  const canModifyCase = permissions.includes(CAN_MODIFY_CASE);

  const submit = async (values: CaseFormValues) => {
    await updateCase.mutateAsync({
      internalApiUpdateCaseDetailsRequest: {
        ...values,
        description: values.description || undefined,
      },
    });
  };

  const handleArchive = async () => {
    const isConfirmed = await dispatch(
      showConfirmationModal({
        title: 'Archive case?',
        description: `"${caseData?.details.name}" will be archived and no longer available.`,
        confirmationText: 'Yes, archive',
      })
    );

    if (!isConfirmed) return;

    try {
      await archiveCase.mutateAsync(handle);
      dispatch(showFeedback({ message: 'Case archived successfully', success: true }));
      navigate(InternalNavigation.cases);
    } catch {
      dispatch(showFeedback({ message: 'Failed to archive case', success: false }));
    }
  };

  return (
    <SalvPage
      title={isCasePending ? <SalvProgressCircle ariaLabel={'Loading Case'} /> : caseData.details.name}
      breadcrumb={{ title: 'Cases', to: InternalNavigation.cases }}
      actions={
        <Has allPermissions={[CAN_ARCHIVE_CASE]}>
          <SalvMenuButton
            size={Size.SMALL}
            ariaLabel='Case actions'
            onAction={async ([action]) => {
              if (action === 'archive') {
                await handleArchive();
              }
            }}
          >
            <MenuItem title='Archive' value='archive' dangerTextColor />
          </SalvMenuButton>
        </Has>
      }
    >
      <div
        style={{
          display: 'flex',
          gap: '8px',
        }}
      >
        <div
          style={{
            flex: '0 1 30%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {isCasePending ? (
            <SalvProgressCircle ariaLabel={'Loading Case'} />
          ) : (
            <Formik
              validationSchema={validationSchema}
              initialValues={initialValues!}
              onSubmit={submit}
              enableReinitialize
            >
              <Form>
                <SalvCard>
                  <SalvCard.Header>Case details</SalvCard.Header>
                  <SalvCard.Body style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <FieldEditor
                      label='Name'
                      field='name'
                      disabled={!canModifyCase}
                      editedField={editedField}
                      setEditedField={setEditedField}
                    >
                      <SalvTextArea required autoSizing minRows={1} />
                    </FieldEditor>
                    <FieldEditor
                      label='Description'
                      field='description'
                      disabled={!canModifyCase}
                      editedField={editedField}
                      setEditedField={setEditedField}
                    >
                      <SalvTextArea autoSizing minRows={2} />
                    </FieldEditor>
                    <FieldEditor
                      label='Rationale'
                      field='rationaleCode'
                      disabled={!canModifyCase}
                      editedField={editedField}
                      setEditedField={setEditedField}
                    >
                      <SalvDropdown options={!isRationaleOptionsPending ? rationaleOptions : []} clearable={false} />
                    </FieldEditor>
                  </SalvCard.Body>
                </SalvCard>
              </Form>
            </Formik>
          )}
          <CasePersons handle={handle} />
        </div>
        <div
          style={{
            flex: '0 1 70%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {isCasePending ? (
            <SalvProgressCircle ariaLabel={'Loading Case'} />
          ) : (
            <CasePendingStatusProvider>
              <CaseManagement caseData={caseData} />
              <CaseNotes caseData={caseData} />
            </CasePendingStatusProvider>
          )}
          <CaseAlerts handle={handle} />
          <Documents entityType={InternalApiUniversalDocumentEntityType.CASE} entityRef={handle} />
        </div>
      </div>
      <div className='m-t-16' />
      <CaseTransactions handle={handle} />
      {isCasePending ? (
        <SalvProgressCircle ariaLabel={'Loading Case'} />
      ) : (
        <div className={'m-t-24 text-secondary'} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <FieldView label='Created by' value={caseData?.createdBy.name} />
          <FieldView label='Created at' value={formatDate(caseData?.createdAt, true)} />
        </div>
      )}
      <div className={'p-b-128'} />
    </SalvPage>
  );
};

export default Case;
